import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaSave, FaPlus, FaShieldAlt, FaSearch, FaBuilding, FaUser } from "react-icons/fa";
import { Button } from 'components/common';
import {
  getPurchaseOrderViewGrants, updatePurchaseOrderViewGrants,
  searchPurchaseOrderViewDepartments, searchPurchaseOrderViewUsers,
} from "../common/settingsApi";

// Admin: chọn Department / User nào được xem TẤT CẢ Purchase Order — ngoài chủ phiếu và
// người ký bước hiện tại (2 nhóm đó luôn tự xem được phiếu liên quan tới mình, không cần
// cấu hình ở đây). Purchase Requisition thì ai cũng xem được nên không cần panel tương tự.
const emptyOption = (id, label, email, siteName) => ({ value: id, label, email, siteName });

// Gom danh sách theo tên site → [[siteName, items], ...]. Phần tử không có site rơi
// vào nhóm cuối "— No site —". Giữ nguyên thứ tự xuất hiện (BE đã order theo site_id, name).
const groupBySite = (list, getKey) => {
  const groups = new Map();
  list.forEach((it) => {
    const key = getKey(it) || "— No site —";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  });
  return Array.from(groups.entries());
};

// 1 khối "chip list + add dropdown" dùng chung cho cả Department và User.
// grouped=true → dropdown và chip list được nhóm theo site (dùng cho Department).
const GrantSection = ({ icon: Icon, title, description, items, onRemove, fetcher, onAdd, placeholder, grouped = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetcher(q);
        setOptions(res?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [open, q, fetcher]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const alreadyAdded = (id) => items.some((i) => String(i.value) === String(id));

  const renderOption = (o) => {
    const added = alreadyAdded(o.value);
    return (
      <div
        key={o.value} role="button" tabIndex={0}
        onClick={() => { if (!added) { onAdd(emptyOption(o.value, o.label, o.email, o.site_name)); setOpen(false); setQ(""); } }}
        className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-transition-colors ${
          added ? "tw-opacity-50 tw-cursor-default" : "tw-cursor-pointer hover:tw-bg-geist-100"
        }`}
      >
        <FaPlus size={9} className="tw-text-geist-fg-3 tw-flex-shrink-0" />
        <span className="tw-text-[13px] tw-text-geist-fg tw-truncate">{o.label}</span>
        {added && <span className="tw-ml-auto tw-text-[10px] tw-text-geist-fg-3 tw-flex-shrink-0">Added</span>}
      </div>
    );
  };

  const groupHeader = (label) => (
    <div className="tw-px-3 tw-py-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-bg-geist-bg-2 tw-sticky tw-top-0 tw-z-10">
      {label}
    </div>
  );

  const renderChip = (i) => (
    <span key={i.value} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-7 tw-pl-2.5 tw-pr-1.5 tw-rounded-xl tw-bg-geist-100 tw-text-[12px] tw-font-medium tw-text-geist-fg-2">
      {i.label}
      <button type="button" onClick={() => onRemove(i.value)}
        className="tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-xl tw-border-0 tw-bg-transparent tw-text-geist-fg-3 hover:tw-bg-geist-200 hover:tw-text-geist-error tw-transition-colors tw-cursor-pointer">
        <i className="bi bi-x-lg tw-text-[8px]" />
      </button>
    </span>
  );

  return (
    <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-p-4">
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-mb-3">
        <div className="tw-flex tw-items-start tw-gap-2.5">
          <span className="tw-inline-flex tw-h-8 tw-w-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-sm tw-bg-geist-100 tw-text-geist-accent">
            <Icon size={13} />
          </span>
          <div>
            <div className="tw-text-sm tw-font-semibold tw-text-geist-fg">{title}</div>
            <p className="tw-text-xs tw-text-geist-fg-3 tw-m-0 tw-mt-0.5 tw-max-w-md">{description}</p>
          </div>
        </div>
        <div ref={ref} className="tw-relative tw-flex-shrink-0">
          <Button variant="secondary" size="sm" leftIcon={FaPlus} onClick={() => setOpen((o) => !o)}>Add</Button>
          {open && (
            <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-w-64 tw-z-50 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-md tw-shadow-geist-menu tw-overflow-hidden">
              <div className="tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border">
                <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg-2 tw-rounded-geist-sm tw-px-2.5 tw-py-1.5">
                  <FaSearch size={11} className="tw-text-geist-fg-3 tw-flex-shrink-0" />
                  <input
                    autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder={placeholder}
                    className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
                  />
                </div>
              </div>
              <div className="tw-max-h-60 tw-overflow-y-auto tw-py-1">
                {loading ? (
                  <div className="tw-px-3 tw-py-3 tw-text-xs tw-text-geist-fg-3 tw-flex tw-items-center tw-gap-2">
                    <i className="bi bi-arrow-repeat tw-animate-spin" /> Searching...
                  </div>
                ) : options.length === 0 ? (
                  <div className="tw-px-3 tw-py-4 tw-text-xs tw-text-geist-fg-3 tw-text-center">No results.</div>
                ) : grouped ? (
                  groupBySite(options, (o) => o.site_name).map(([site, opts]) => (
                    <div key={site}>
                      {groupHeader(site)}
                      {opts.map(renderOption)}
                    </div>
                  ))
                ) : (
                  options.map(renderOption)
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="tw-text-xs tw-text-geist-fg-3 tw-italic tw-py-2">None granted.</div>
      ) : grouped ? (
        <div className="tw-space-y-2.5">
          {groupBySite(items, (i) => i.siteName).map(([site, group]) => (
            <div key={site}>
              <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-geist-fg-3">
                <FaBuilding size={9} /> {site}
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-1.5">{group.map(renderChip)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">{items.map(renderChip)}</div>
      )}
    </div>
  );
};
GrantSection.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.node,
  description: PropTypes.node,
  items: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
    email: PropTypes.string,
    siteName: PropTypes.string,
  })).isRequired,
  onRemove: PropTypes.func.isRequired,
  fetcher: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  grouped: PropTypes.bool,
};

const PoAccessPanel = () => {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => {
    setLoading(true);
    getPurchaseOrderViewGrants()
      .then((res) => {
        setDepartments((res?.data?.departments || []).map((d) => emptyOption(d.value, d.label, undefined, d.site_name)));
        setUsers((res?.data?.users || []).map((u) => emptyOption(u.value, u.label, u.email)));
        setDirty(false);
      })
      .catch(() => toast.error("Failed to load PO access settings."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const addDept = (d) => { setDepartments((prev) => [...prev, d]); setDirty(true); };
  const removeDept = (id) => { setDepartments((prev) => prev.filter((d) => d.value !== id)); setDirty(true); };
  const addUser = (u) => { setUsers((prev) => [...prev, u]); setDirty(true); };
  const removeUser = (id) => { setUsers((prev) => prev.filter((u) => u.value !== id)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await updatePurchaseOrderViewGrants(departments.map((d) => d.value), users.map((u) => u.value));
      toast.success("PO access settings saved.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to save PO access settings.");
    } finally {
      setSaving(false);
    }
  };

  const total = useMemo(() => departments.length + users.length, [departments, users]);

  if (loading) return <div className="tw-text-sm tw-text-geist-fg-3 tw-py-8 tw-text-center">Loading...</div>;

  return (
    <div className="tw-space-y-4">
      {/* Toolbar cố định phía trên — Save luôn thấy, không phải cuộn xuống cuối. */}
      <div className="tw-flex tw-items-start tw-gap-3">
        <span className="tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-geist-md tw-bg-geist-100 tw-text-geist-accent">
          <FaShieldAlt size={14} />
        </span>
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-sm tw-font-semibold tw-text-geist-fg">PO access</span>
            <span className="tw-text-[11px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2">{total} granted</span>
          </div>
          <p className="tw-text-xs tw-text-geist-fg-3 tw-m-0 tw-mt-0.5 tw-max-w-lg">
            Purchase Requisitions are visible to everyone. Purchase Orders are private by default — only the
            requestor and the current signer can see one. Grant a whole Department or specific Users below to
            let them view <span className="tw-font-medium">all</span> Purchase Orders.
          </p>
        </div>
        <Button variant="primary" size="sm" leftIcon={FaSave} disabled={saving || !dirty} onClick={save} className="tw-flex-shrink-0">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <GrantSection
        icon={FaBuilding}
        title="Departments"
        description="Everyone in these departments can view all Purchase Orders."
        items={departments}
        onRemove={removeDept}
        onAdd={addDept}
        fetcher={searchPurchaseOrderViewDepartments}
        placeholder="Search departments…"
        grouped
      />

      <GrantSection
        icon={FaUser}
        title="Individual users"
        description="These users can view all Purchase Orders regardless of department."
        items={users}
        onRemove={removeUser}
        onAdd={addUser}
        fetcher={searchPurchaseOrderViewUsers}
        placeholder="Search by name / email…"
      />
    </div>
  );
};

export default PoAccessPanel;
